import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { amber, green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import SnackbarContent, {
  SnackbarContentProps,
} from "@mui/material/SnackbarContent";
import WarningIcon from "@mui/icons-material/Warning";
import { makeStyles } from "@mui/styles";

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

const useStyles = makeStyles((theme) => ({
  success: {
    backgroundColor: green[600],
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  info: {
    backgroundColor: theme.palette.primary.main,
  },
  warning: {
    backgroundColor: amber[700],
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  content: {
    display: "flex",
    flexDirection: "row",
  },
  contentMessage: {
    flex: 1,
  },
  message: {
    display: "flex",
    alignItems: "center",
  },
}));

export type SnackbarVariant = keyof typeof variantIcon;

export interface SnackBarContentProps extends Omit<
  SnackbarContentProps,
  "message" | "action" | "classes" | "variant"
> {
  message?: React.ReactNode;
  onClose?: (event: React.SyntheticEvent | Event, reason?: string) => void;
  variant: SnackbarVariant;
}

const SnackBarContent = React.forwardRef<HTMLDivElement, SnackBarContentProps>(
  function SnackBarContent({ message, onClose, variant, ...other }, ref) {
    const classes = useStyles();

    const Icon = variantIcon[variant];

    return (
      <SnackbarContent
        ref={ref}
        className={[classes[variant], classes.content].join(" ")}
        classes={{
          message: classes.contentMessage,
        }}
        aria-describedby="client-snackbar"
        message={
          <span id="client-snackbar" className={classes.message}>
            <Icon className={[classes.icon, classes.iconVariant].join(" ")} />
            {message}
          </span>
        }
        action={[
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={onClose}
          >
            <CloseIcon className={classes.icon} />
          </IconButton>,
        ]}
        {...other}
      />
    );
  }
);

export default SnackBarContent;
